// ---------------------------------------------------------------------------
// Overall "All Schools Today" — pure helpers
// ---------------------------------------------------------------------------
// Normalises the super-admin overall endpoint / bell-queue payloads into the
// shape the overall view renders, and ports the category / colour / segment
// logic from prompt/overall.html. No React, no DOM.
// ---------------------------------------------------------------------------
import {minutes, nowMinutes, to24h} from "./bellStatusUtils";

// Brand palette (matches prompt/overall.html).
export const PALETTE = {
    ink: "#09152B",
    blue: "#0B66DB",
    blueInk: "#084FA8",
    green: "#047E4F",
    greenInk: "#035C3A",
    gold: "#FFB305",
    rust: "#C2410C",
    rustInk: "#9A3412",
    grey: "#5B6475",
    line: "#E1E7F0",
    scheduled: "#DCE3EE",
};

const LOGO_BGS = [PALETTE.blue, PALETTE.green, PALETTE.ink, "#7A4B00"];

// First-match accessor across a list of candidate keys.
function pick(obj, keys, fallback = null) {
    if (!obj) return fallback;
    for (const k of keys) {
        const v = obj[k];
        if (v !== undefined && v !== null && v !== "") return v;
    }
    return fallback;
}

const toNum = (v, d = null) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : d;
};

// Minutes-since-midnight for a bell time in any accepted shape ("10:15",
// "10:15 AM", "13:30"). Returns null when unparseable.
function bellMinutes(raw) {
    const t = to24h(raw);
    if (t) return minutes(t);
    const m = String(raw || "").match(/^(\d{1,2}):(\d{2})$/);
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

// "h:mm AM/PM" (the design's clock format).
export function fmtClock(min) {
    if (min == null || !Number.isFinite(min)) return "—";
    const h = Math.floor(min / 60);
    const mm = min % 60;
    const ap = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${mm < 10 ? "0" + mm : mm} ${ap}`;
}

// Two-letter initials for the logo tile (skips a leading "Shree").
function initialsOf(name) {
    const clean = String(name || "").replace(/^(Shree|श्री)\s+/i, "");
    const words = clean.split(/\s+/).filter(Boolean);
    const letters = words.map((w) => w[0]).filter((c) => /[A-Za-zऀ-ॿ]/.test(c));
    return (letters.slice(0, 2).join("") || "?").toUpperCase();
}

// Per-bell visual kind, preferring an explicit backend status and otherwise
// deriving from the scheduled time vs now and device state.
//   rung | late | missed | offline | next | upcoming
function deriveKind(status, timeMin, now, online, offlineSince, isFirstFuture) {
    const v = String(status || "").toUpperCase();
    if (v.includes("LATE")) return "late";
    if (v.includes("PLAY") || v.includes("RANG") || v.includes("RUNG") || v.includes("DONE") || v.includes("SUCCESS")) {
        return "rung";
    }
    if (v.includes("FAIL") || v.includes("MISS") || v.includes("ERROR")) return "missed";
    if (v.includes("OFFLINE")) return "offline";

    // No definitive status — derive it.
    const due = timeMin != null && timeMin <= now;
    const offAt = online === false && (offlineSince == null || (timeMin != null && timeMin > offlineSince));
    if (due) return "missed"; // past & nothing recorded → missed
    if (offAt) return "offline"; // future, but device is down
    if (isFirstFuture) return "next";
    return "upcoming";
}

// Normalise one bell/queue row into a timeline entry.
function normalizeBell(raw) {
    const timeRaw = pick(raw, ["hourTime", "time", "scheduledTime", "scheduled_at", "scheduled"]);
    const timeMin = bellMinutes(timeRaw);
    return {
        name: pick(raw, ["name", "label", "bellName"], "Bell"),
        type: pick(raw, ["type", "bellType"], null),
        timeMin,
        rawStatus: pick(raw, ["status", "state"], null),
        playedAtMin: (() => {
            const p = pick(raw, ["playedAt", "actualAt", "actual_at"]);
            if (!p) return null;
            const d = new Date(p);
            if (!Number.isNaN(d.getTime())) return d.getHours() * 60 + d.getMinutes();
            return bellMinutes(p);
        })(),
        lateBy: toNum(pick(raw, ["lateBy", "delayMin", "delay_min"]), null),
    };
}

// Category → chip / accent / status label (ported from design look()).
export function categoryLook(cat) {
    if (cat === "offline") {
        return {status: "Offline", chipBg: "#E8EBF0", chipFg: "#3B4354", accent: PALETTE.grey, border: "#D5DBE5", pctColor: PALETTE.grey};
    }
    if (cat === "missed") {
        return {status: "Missed bell", chipBg: "#FDE7DC", chipFg: PALETTE.rustInk, accent: PALETTE.rust, border: "#F2C9B5", pctColor: PALETTE.rustInk};
    }
    if (cat === "done") {
        return {status: "Completed", chipBg: "#E3EEFC", chipFg: PALETTE.blueInk, accent: PALETTE.blue, border: PALETTE.line, pctColor: PALETTE.blueInk};
    }
    return {status: "On schedule", chipBg: "#DDF3E8", chipFg: PALETTE.greenInk, accent: PALETTE.green, border: PALETTE.line, pctColor: PALETTE.greenInk};
}

// Segment-bar colour for a bell kind (ported from design segColor()).
export function segColor(kind) {
    if (kind === "rung" || kind === "late") return {bg: PALETTE.green, blink: false};
    if (kind === "missed") return {bg: PALETTE.rust, blink: false};
    if (kind === "next") return {bg: PALETTE.gold, blink: true};
    if (kind === "offline") return {bg: "#CBD3DF", blink: false};
    return {bg: PALETTE.scheduled, blink: false};
}

// Per-bell timeline chip styling for the drawer (ported from design).
export function bellRowLook(kind, lateBy) {
    const map = {
        rung: {state: "Rang", chipBg: "#DDF3E8", chipFg: PALETTE.greenInk, dotBg: PALETTE.green, dotBorder: PALETTE.green, timeColor: PALETTE.ink, blink: false},
        late: {state: `Rang +${lateBy || 1} min`, chipBg: "#FFF1D6", chipFg: "#7A4B00", dotBg: PALETTE.green, dotBorder: PALETTE.gold, timeColor: PALETTE.ink, blink: false},
        missed: {state: "Missed", chipBg: "#FDE7DC", chipFg: PALETTE.rustInk, dotBg: PALETTE.rust, dotBorder: PALETTE.rust, timeColor: PALETTE.rustInk, blink: false},
        next: {state: "Next", chipBg: PALETTE.gold, chipFg: PALETTE.ink, dotBg: PALETTE.gold, dotBorder: PALETTE.ink, timeColor: PALETTE.ink, blink: true},
        upcoming: {state: "Scheduled", chipBg: "#EEF2F7", chipFg: "#3B4354", dotBg: "#FFFFFF", dotBorder: "#9AA6B8", timeColor: "#4A5568", blink: false},
        offline: {state: "Device offline", chipBg: "#E8EBF0", chipFg: "#3B4354", dotBg: "#FFFFFF", dotBorder: PALETTE.grey, timeColor: "#4A5568", blink: false},
    };
    return map[kind] || map.upcoming;
}

// Normalise a single school's today status from the overall payload. `bells`
// may come inline (preferred) or be injected later from the bell-queue API.
export function normalizeSchool(raw, idx, injectedBells) {
    const id = String(pick(raw, ["schoolId", "id", "value"], idx));
    const name = pick(raw, ["schoolName", "name", "label"], `School ${id}`);
    const online = pick(raw, ["deviceOnline", "online", "isOnline"], true) !== false;
    const offlineSinceRaw = pick(raw, ["offlineSince", "offlineAt", "offline_at"]);
    const offlineSince = offlineSinceRaw != null ? bellMinutes(offlineSinceRaw) : null;

    const rawBells = injectedBells || pick(raw, ["bells", "queue", "schedule", "timeline", "periods"], []);
    const now = nowMinutes();

    let bells = (Array.isArray(rawBells) ? rawBells : [])
        .map(normalizeBell)
        .filter((b) => b.timeMin != null)
        .sort((a, b) => a.timeMin - b.timeMin);

    let played = 0;
    let late = 0;
    let missed = 0;
    let nextIdx = -1;
    let lastIdx = -1;
    let firstFutureMarked = false;

    bells = bells.map((b, k) => {
        const isFirstFuture = !firstFutureMarked && b.timeMin > now && online;
        const kind = deriveKind(b.rawStatus, b.timeMin, now, online, offlineSince, isFirstFuture);
        if (kind === "next") firstFutureMarked = true;
        if (kind === "rung" || kind === "late") {
            played += 1;
            if (kind === "late") late += 1;
            lastIdx = k;
        } else if (kind === "missed") {
            missed += 1;
        } else if (kind === "next" && nextIdx < 0) {
            nextIdx = k;
        }
        return {...b, kind};
    });

    // Prefer backend-provided counts when no per-bell list was available.
    const haveBells = bells.length > 0;
    const scheduled = haveBells ? bells.length : toNum(pick(raw, ["scheduled", "total", "totalBells"]), 0);
    if (!haveBells) {
        played = toNum(pick(raw, ["played", "rung"]), 0);
        late = toNum(pick(raw, ["late"]), 0);
        missed = toNum(pick(raw, ["missed", "failed"]), 0);
    }

    const cat = !online
        ? "offline"
        : missed > 0
        ? "missed"
        : played >= scheduled && scheduled > 0
        ? "done"
        : nextIdx < 0 && haveBells && played + missed >= scheduled
        ? "done"
        : "live";

    const next = nextIdx >= 0 ? bells[nextIdx] : null;
    const last = lastIdx >= 0 ? bells[lastIdx] : null;

    return {
        id,
        name,
        nameNe: pick(raw, ["schoolNameNe", "name_ne", "nameNe", "label_ne"], null),
        logo: pick(raw, ["schoolLogo", "logo", "logoUrl", "image"], null),
        address: pick(raw, ["address", "schoolAddress", "district"], ""),
        online,
        offlineSince,
        initials: initialsOf(name),
        logoBg: LOGO_BGS[idx % LOGO_BGS.length],
        bells,
        hasBells: haveBells,
        played,
        late,
        missed,
        scheduled,
        nextIdx,
        lastIdx,
        next,
        last,
        nextMin: next ? next.timeMin : 99999,
        cat,
        pct: scheduled ? Math.round((played / scheduled) * 100) : 0,
    };
}

// Normalise the whole overall payload into { schools, summary }. Accepts a bare
// array, { schools }, { data } or { items }, and computes the summary from the
// schools when the backend doesn't send one.
export function normalizeOverall(payload) {
    const list =
        (Array.isArray(payload) && payload) ||
        pick(payload, ["schools", "data", "items", "content"], []) ||
        [];

    const schools = (Array.isArray(list) ? list : []).map((s, i) => normalizeSchool(s, i));

    let rung = 0;
    let scheduled = 0;
    let missed = 0;
    let online = 0;
    let completed = 0;
    let attention = 0;
    schools.forEach((s) => {
        rung += s.played;
        scheduled += s.scheduled;
        missed += s.missed;
        if (s.online) online += 1;
        if (s.cat === "done") completed += 1;
        if (s.cat === "missed" || s.cat === "offline") attention += 1;
    });

    const summary = {
        active: schools.length,
        online,
        rung,
        scheduled,
        completed,
        attention,
        pct: scheduled ? Math.round((rung / scheduled) * 100) : 0,
        missPct: scheduled ? Math.round((missed / scheduled) * 100) : 0,
    };

    // Allow the backend to override the summary if it ships one.
    const apiSummary = pick(payload, ["summary", "totals"], null);
    return {schools, summary: apiSummary ? {...summary, ...apiSummary} : summary};
}

// Build the drawer model (3 stat tiles + timeline rows) for a selected school.
export function buildDrawer(school) {
    if (!school) return null;
    const rows = (school.bells || []).map((b) => {
        const look = bellRowLook(b.kind, b.lateBy);
        return {
            time: fmtClock(b.timeMin),
            label: b.name,
            sub: b.type ? String(b.type).replace(/_/g, " ").toLowerCase() : "",
            ...look,
        };
    });
    return {
        ...school,
        rows,
        missColor: school.missed > 0 ? PALETTE.rustInk : PALETTE.ink,
        device: school.online ? "Online" : "Offline",
        devColor: school.online ? PALETTE.greenInk : "#3B4354",
    };
}

// Filter + sort the school cards for the grid.
export function filterSort(schools, {filter, query, sort}) {
    const q = String(query || "").toLowerCase().trim();
    const isAttn = (c) => c.cat === "missed" || c.cat === "offline";
    const list = schools.filter((c) => {
        if (filter === "live" && c.cat !== "live") return false;
        if (filter === "done" && c.cat !== "done") return false;
        if (filter === "attn" && !isAttn(c)) return false;
        if (q && `${c.name} ${c.nameNe || ""} ${c.address}`.toLowerCase().indexOf(q) < 0) return false;
        return true;
    });
    const rank = {offline: 0, missed: 1, live: 2, done: 3};
    return [...list].sort((a, b) => {
        if (sort === "name") return a.name.localeCompare(b.name);
        if (sort === "progress") return b.pct - a.pct;
        if (sort === "next") return a.nextMin - b.nextMin;
        return (rank[a.cat] - rank[b.cat]) || a.name.localeCompare(b.name);
    });
}

export function filterCounts(schools) {
    const isAttn = (c) => c.cat === "missed" || c.cat === "offline";
    return {
        all: schools.length,
        live: schools.filter((c) => c.cat === "live").length,
        done: schools.filter((c) => c.cat === "done").length,
        attn: schools.filter(isAttn).length,
    };
}
